<template>
  <svg
    class="arcs"
    :viewBox="`0 0 ${layout.width} ${layout.height}`"
    role="img"
    aria-label="Stages in a line. Small arcs reach the next stage. Larger arcs jump further back."
  >
    <defs>
      <marker
        v-for="marker in layout.markers"
        :id="marker.id"
        :key="marker.id"
        viewBox="0 0 10 10"
        refX="10"
        refY="5"
        markerWidth="11"
        markerHeight="11"
        markerUnits="userSpaceOnUse"
        orient="auto"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" :fill="marker.color" />
      </marker>
    </defs>
    <path
      v-for="(arc, i) in layout.arcs"
      :key="i"
      :d="arc.d"
      :stroke="arc.color"
      fill="none"
      stroke-width="2"
      stroke-linecap="round"
      :marker-end="`url(#${arc.marker})`"
    />
    <g v-for="node in layout.nodes" :key="node.label">
      <rect
        :x="node.x"
        :y="node.y"
        :width="node.w"
        :height="node.h"
        rx="8"
        fill="#f7f4ef"
        stroke="#8a8175"
      />
      <text
        :x="node.x + node.w / 2"
        :y="node.y + node.h / 2"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="#1c1915"
        font-size="13"
        font-family="inherit"
      >
        <tspan
          v-for="(line, li) in node.lines"
          :key="li"
          :x="node.x + node.w / 2"
          :dy="li === 0 ? node.dy : 15"
        >
          {{ line }}
        </tspan>
      </text>
    </g>
  </svg>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  code: { type: String, default: '' },
})

// One step back is the palest red. Each extra stage back is a deeper red.
const RED = {
  1: '#f8d4d4',
  2: '#f0a3a3',
  3: '#e06666',
  4: '#c62828',
  5: '#8e0000',
}

const GAP = 148
const BOX_W = 128
const BOX_H = 46
const PAD_X = 24

function wrap(label) {
  if (label.length <= 14) return [label]
  const cut = label.lastIndexOf(' ', 14)
  const at = cut > 0 ? cut : 14
  return [label.slice(0, at), label.slice(at).trim()]
}

function parse(code) {
  const nodes = []
  const forward = []
  const back = []
  for (const raw of String(code || '').split('\n')) {
    const line = raw.trim()
    if (!line || line.startsWith('%%')) continue
    if (line.startsWith('>')) {
      const [, from, to, color] = line.match(/^>\s+(\d+)\s+(\d+)\s+(#[0-9a-fA-F]{6})\b/) || []
      if (from != null) forward.push({ from: Number(from), to: Number(to), color })
    } else if (line.startsWith('<')) {
      const [, from, to] = line.match(/^<\s+(\d+)\s+(\d+)/) || []
      if (from != null) back.push({ from: Number(from), to: Number(to) })
    } else {
      nodes.push(line)
    }
  }
  return { nodes, forward, back }
}

// sweep 0 puts feedback above the row and forward steps below it.
function arcPath(x1, y, x2, y2, sweep) {
  const r = Math.abs(x2 - x1) / 2
  return `M ${x1} ${y} A ${r} ${r} 0 0 ${sweep} ${x2} ${y2}`
}

// Several arcs can end on one stage. Spread those tips across the top of the
// box so the arrowheads do not sit on top of each other.
function landingOffsets(edges) {
  const groups = new Map()
  for (const edge of edges) {
    if (!groups.has(edge.to)) groups.set(edge.to, [])
    groups.get(edge.to).push(edge)
  }
  const offset = new Map()
  for (const group of groups.values()) {
    group.sort((a, b) => a.from - b.from)
    const n = group.length
    // Use most of the box width so neighboring heads have a visible gap.
    const maxSpan = BOX_W - 16
    const step = n < 2 ? 0 : maxSpan / (n - 1)
    const start = -((n - 1) * step) / 2
    group.forEach((edge, i) => offset.set(edge, start + i * step))
  }
  return offset
}

const layout = computed(() => {
  const { nodes: labels, forward, back } = parse(props.code)
  const n = labels.length
  const maxBack = back.reduce((m, e) => Math.max(m, Math.abs(e.from - e.to)), 1)
  const top = 20 + (maxBack * GAP) / 2
  const boxY = top
  const bottom = boxY + BOX_H + GAP / 2 + 16
  const width = PAD_X * 2 + Math.max(n - 1, 0) * GAP + BOX_W
  const cx = (i) => PAD_X + BOX_W / 2 + i * GAP

  const markers = []
  const markerId = (color) => {
    const id = `arc-${color.slice(1)}`
    if (!markers.some((m) => m.id === id)) markers.push({ id, color })
    return id
  }

  const arcs = []
  const tipOffset = landingOffsets(back)
  // Largest feedback arcs first, so a short arc stays visible where they meet.
  const backs = [...back].sort((a, b) => Math.abs(b.from - b.to) - Math.abs(a.from - a.to))
  for (const edge of backs) {
    const span = Math.abs(edge.from - edge.to)
    const color = RED[span] || RED[5]
    arcs.push({
      d: arcPath(cx(edge.from), boxY, cx(edge.to) + (tipOffset.get(edge) || 0), boxY, 0),
      color,
      marker: markerId(color),
    })
  }
  for (const edge of forward) {
    arcs.push({
      d: arcPath(cx(edge.from), boxY + BOX_H, cx(edge.to), boxY + BOX_H, 0),
      color: edge.color,
      marker: markerId(edge.color),
    })
  }

  const nodes = labels.map((label, i) => {
    const lines = wrap(label)
    return {
      label,
      lines,
      x: PAD_X + i * GAP,
      y: boxY,
      w: BOX_W,
      h: BOX_H,
      dy: lines.length === 1 ? 0 : -7,
    }
  })

  return { width, height: bottom, arcs, nodes, markers }
})
</script>

<style scoped>
.arcs {
  display: block;
  width: 100%;
  height: auto;
  margin: 0.6rem 0 0.2rem;
}
</style>
